import { Ref } from 'react';
import type { FC } from 'react';
// The closer the import is to the file the lower it should be in this list.
import { StyledSubNavigation, StyledCardArticle } from './SubNavigation.styles';
import { IconBlock, Link } from '@marriott/mi-ui-library';
import { MAX_ITEMS_IN_A_COLUMN } from './SubNavigation.types';
import type { MegaMenuItem, SubNavigationProps } from './SubNavigation.types';
import { getClickTrackValue } from '../../../utils/TrackingPropertyUtils';
import { DEFAULT_TRACKING_VALUES } from '../../../utils/constants';
import clsx from 'clsx';

export const SubNavigation: FC<SubNavigationProps> = ({
  subNavItems,
  isArticleCardEnabled,
  i18nOpensInNewTab,
  cardArticleData,
  maxItemsPerColumn = MAX_ITEMS_IN_A_COLUMN,
  dynamicData,
  forwardedRefs,
  articleCardRef,
  primaryParentLabel = DEFAULT_TRACKING_VALUES.LABEL_NULLISH,
  trackingProperties,
}) => {
  const subNavItemsLength: number = subNavItems?.length || 0;

  return (
    <StyledSubNavigation
      subNavLength={subNavItemsLength}
      maxItemsPerColumn={maxItemsPerColumn}
      data-testid="sub-navigation-item"
      className="sub-nav__nav__container"
    >
      <ul className="sub-navigation-list" role="list" aria-label="Sub Navigation List">
        {subNavItems?.map((item: MegaMenuItem, index: number) => {
          const sanitizedTitle = (item?.title ?? '').replace(/<\/?p>/gi, '');
          return (
            <li
              className={clsx('list-item t-label-alt-s', { 'last-list-item': (index + 1) % maxItemsPerColumn === 0 })}
              key={index}
            >
              <Link
                linkHref={item?.url}
                ref={forwardedRefs[index] as Ref<HTMLAnchorElement>}
                linkClassName="list-item-link custom_click_track"
                target={item.openInaNewTab === 'true' ? '_blank' : '_self'}
                rel={item.openInaNewTab === 'true' ? 'noopener noreferrer' : undefined}
                custom_click_track_value={getClickTrackValue({
                  trackingProperties: {
                    trackingContentPosition: trackingProperties?.trackingContentPosition,
                  },
                  url: item?.url,
                  fallbacks: {
                    position: primaryParentLabel,
                    description: item?.title ?? DEFAULT_TRACKING_VALUES.LABEL_NULLISH,
                  },
                })}
              >
                <span dangerouslySetInnerHTML={{ __html: sanitizedTitle }} />
                {item.openInaNewTab === 'true' && <span className="sr-only">{i18nOpensInNewTab}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
      {isArticleCardEnabled && (
        <StyledCardArticle>
          <IconBlock
            ctaLink={cardArticleData?.ctalink ?? ''}
            assetVariation="image"
            ctaType="secondaryButton"
            variation="test"
            contentAlignment="left"
            ctaLinkText={cardArticleData?.ctalabel}
            descriptionText={cardArticleData?.descriptiontext}
            dynamicMedia={dynamicData}
            header={cardArticleData?.titletext}
            isRichText
            openInNewTab={cardArticleData?.openinanewtab === 'true'}
            componentId="cardarticle"
            ref={articleCardRef}
            trackingProperties={{
              clickTrack: true,
              location: trackingProperties?.trackingContentPosition || primaryParentLabel,
              trackingDescription: cardArticleData?.ctalabel || '',
              trackingContentPosition: trackingProperties?.trackingContentPosition || primaryParentLabel,
            }}
          />
        </StyledCardArticle>
      )}
    </StyledSubNavigation>
  );
};
